import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Section, Card, Button } from '../components/ui';
import Header from './Header';
import Footer from './Footer';
import consultingImage from '../assets/images/home-consulting.jpg';
import capitalImage from '../assets/images/home-capital.jpg';
import technologyImage from '../assets/images/home-technology.jpg';
import './ServicesHubPage.css';

const lines = [
    {
        slug: 'consulting',
        title: 'Consulting',
        description:
            'Change management consulting: operating model, adoption, and governance so transformation lands in the organisation.',
        image: consultingImage,
        imageAlt: 'Frosted glass panels with an abstract process graph',
        cta: 'Explore Consulting',
    },
    {
        slug: 'capital',
        title: 'Capital',
        description:
            'Angel investing and private equity—capital plus judgment, aligned on milestones and long-term value.',
        image: capitalImage,
        imageAlt: 'Stacked translucent glass blocks suggesting compound growth',
        cta: 'Explore Capital',
    },
    {
        slug: 'technology',
        title: 'Technology',
        description:
            'Automation, AI, and analytics: tools we deliver and build so leaders see what matters and teams keep pace.',
        image: technologyImage,
        imageAlt: 'Macro photograph of fibre-optic strands catching daylight',
        cta: 'Explore Technology',
    },
];

const ServicesHubPage = () => {
    const navigate = useNavigate();

    return (
        <div className="services-hub-page">
            <Header variant="marketing" />
            <Section variant="large" className="services-hub-hero anchor-target">
                <div className="services-hub-hero-inner">
                    <motion.p
                        className="services-hub-eyebrow"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                    >
                        Services
                    </motion.p>
                    <motion.h1
                        className="services-hub-title"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                    >
                        Consulting, capital, and technology—one partnership logic.
                    </motion.h1>
                    <motion.p
                        className="services-hub-lede"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        MotionSpace combines change management consulting, angel and private-equity investment, and
                        hands-on delivery of automation, AI, and analytics. Each line has its own page—pick where you
                        want to start, or contact us to shape a blended mandate.
                    </motion.p>
                </div>
            </Section>

            <Section variant="default" className="services-hub-grid-section">
                <div className="services-hub-cards">
                    {lines.map((line, index) => (
                        <motion.div
                            key={line.slug}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.55, delay: index * 0.08 }}
                        >
                            <Card variant="interactive" className="services-hub-card">
                                <div className="services-hub-card-image-wrap">
                                    <img
                                        src={line.image}
                                        alt={line.imageAlt}
                                        className="services-hub-card-image"
                                    />
                                    <div className="services-hub-card-image-overlay" />
                                </div>
                                <div className="services-hub-card-body">
                                    <h2 className="services-hub-card-title">{line.title}</h2>
                                    <p className="services-hub-card-desc">{line.description}</p>
                                    <Button
                                        variant="primary"
                                        size="md"
                                        className="services-hub-card-link"
                                        onClick={() => navigate(`/services/${line.slug}`)}
                                    >
                                        {line.cta}
                                    </Button>
                                </div>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </Section>

            <Section variant="small" className="services-hub-bottom">
                <motion.div
                    className="services-hub-bottom-inner"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <p className="services-hub-bottom-text">Not sure where to start?</p>
                    <Button variant="outline" size="lg" onClick={() => navigate('/contact')}>
                        Tell us about your context
                    </Button>
                </motion.div>
            </Section>

            <Footer />
        </div>
    );
};

export default ServicesHubPage;
